import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { UserService, PaginatedUsersResponse, UpdateUserRequest, RegisterRequest } from '../../services/user.service';
import { SecurityService } from '../../services/security.service';
import { User } from '../../../models/user';
import { PageHeaderComponent } from '../../shared/ui/components';
import { HlmButton } from '@spartan-ng/helm/button';
import { HlmBadge } from '@spartan-ng/helm/badge';
import { HlmCard } from '@spartan-ng/helm/card';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { HlmIcon } from '@spartan-ng/helm/icon';
import {
  lucidePlus, lucideX, lucideSearch, lucideChevronDown, lucideDownload,
  lucideUsers, lucideUser, lucideShield, lucideShoppingBag, lucideCheckSquare,
  lucideBriefcase, lucideUserCog, lucidePencil, lucideLock, lucideUnlock, lucideTrash2,
  lucideEye, lucideEyeOff,
} from '@ng-icons/lucide';

interface Role {
  key: string;
  name: string;
}

@Component({
  selector: 'app-user-management',
  standalone: true,
  imports: [CommonModule, FormsModule, PageHeaderComponent, HlmButton, HlmBadge, HlmCard, NgIcon, HlmIcon],
  providers: [provideIcons({
    lucidePlus, lucideX, lucideSearch, lucideChevronDown, lucideDownload,
    lucideUsers, lucideUser, lucideShield, lucideShoppingBag, lucideCheckSquare,
    lucideBriefcase, lucideUserCog, lucidePencil, lucideLock, lucideUnlock, lucideTrash2,
    lucideEye, lucideEyeOff,
  })],
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.css']
})
export class UserManagementComponent implements OnInit {
  // Expose Math for template use
  Math = Math;
  
  // Data properties
  users: User[] = [];
  filteredUsers: User[] = [];
  currentUser: Partial<User> = {
    id: 0,
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    role: '',
    password: '',
    isActive: true
  };
  userToDelete: User | null = null;

  // Pagination properties
  currentPage = 1;
  pageSize = 20;
  totalCount = 0;
  totalPages = 0;

  // UI state properties
  isLoading = false;
  isSubmitting = false;
  isDeleting = false;
  showUserModal = false;
  showDeleteModal = false;
  
  // Security profile properties
  showSecurityModal = false;
  selectedUserForSecurity: User | null = null;
  userSecurityProfile: any = null;
  isLoadingSecurityProfile = false;
  showPasswordField = false;
  showPassword = false;
  isEditMode = false;

  // Messages
  successMessage = '';
  errorMessage = '';

  // Filter properties
  searchTerm = '';
  selectedRole = '';

  // Available roles
  availableRoles: Role[] = [
    { key: 'admin', name: 'Administrator' },
    { key: 'shopee_docs', name: 'Shopee Documents' },
    { key: 'ShopeeChecker', name: 'Shopee Checker' },
    { key: 'ForwardingAdmin', name: 'Forwarding Admin' },
    { key: 'ForwardingUser', name: 'Forwarding User' },
    { key: 'user', name: 'Regular User' }
  ];

  constructor(
    private userService: UserService,
    private securityService: SecurityService
  ) {
    console.log('UserManagementComponent constructor called');
  }

  ngOnInit(): void {
    console.log('UserManagementComponent ngOnInit called');
    this.loadUsers();
  }

  // Data loading methods
  loadUsers(): void {
    console.log('loadUsers() called');
    this.isLoading = true;
    this.clearMessages();

    this.userService.getUsers(this.currentPage, this.pageSize).subscribe({
      next: (response: PaginatedUsersResponse) => {
        console.log('Users loaded successfully:', response);
        this.users = response.users;
        this.totalCount = response.totalCount;
        this.totalPages = response.totalPages;
        this.currentPage = response.pageNumber;
        this.applyFilters();
        this.isLoading = false;
      },
      error: (error: any) => {
        console.error('Error loading users:', error);
        this.errorMessage = 'Failed to load users. Please try again.';
        this.isLoading = false;
      }
    });
  }

  // Pagination methods
  loadPage(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.loadUsers();
    }
  }

  nextPage(): void {
    this.loadPage(this.currentPage + 1);
  }

  previousPage(): void {
    this.loadPage(this.currentPage - 1);
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const startPage = Math.max(1, this.currentPage - 2);
    const endPage = Math.min(this.totalPages, this.currentPage + 2);
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }

  // Filter methods
  applyFilters(): void {
    this.filteredUsers = this.users.filter(user => {
      const matchesSearch = !this.searchTerm || 
        user.firstName.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        user.lastName.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        user.username.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        (user.email && user.email.toLowerCase().includes(this.searchTerm.toLowerCase()));

      const matchesRole = !this.selectedRole || user.role === this.selectedRole;

      return matchesSearch && matchesRole;
    });
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.selectedRole = '';
    this.applyFilters();
  }

  // Modal management methods
  openCreateUserModal(): void {
    this.isEditMode = false;
    this.currentUser = {
      id: 0,
      firstName: '',
      lastName: '',
      username: '',
      email: '',
      role: '',
      password: '',
      isActive: true
    };
    this.showPasswordField = false;
    this.showPassword = false;
    this.showUserModal = true;
    this.clearMessages();
  }

  editUser(user: User): void {
    this.isEditMode = true;
    this.currentUser = { ...user, password: '' };
    this.showPasswordField = false;
    this.showPassword = false;
    this.showUserModal = true;
    this.clearMessages();
  }

  closeUserModal(): void {
    this.showUserModal = false;
    this.isEditMode = false;
    this.showPasswordField = false;
    this.showPassword = false;
    this.clearMessages();
  }

  confirmDeleteUser(user: User): void {
    this.userToDelete = user;
    this.showDeleteModal = true;
    this.clearMessages();
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.userToDelete = null;
  }

  // Password visibility methods
  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  togglePasswordField(): void {
    this.showPasswordField = !this.showPasswordField;
    if (!this.showPasswordField) {
      this.currentUser.password = '';
    }
  }

  // User CRUD operations
  saveUser(): void {
    this.isSubmitting = true;
    this.clearMessages();

    if (this.isEditMode && this.currentUser.id) {
      // Update existing user
      const updateData: UpdateUserRequest = {
        username: this.currentUser.username!,
        firstName: this.currentUser.firstName!,
        lastName: this.currentUser.lastName!,
        email: this.currentUser.email,
        role: this.currentUser.role as 'admin' | 'user' | 'shopee_docs' | 'ShopeeChecker',
        isActive: this.currentUser.isActive ?? true
      };

      if (this.showPasswordField && this.currentUser.password) {
        updateData.password = this.currentUser.password;
      }

      this.userService.updateUser(this.currentUser.id, updateData).subscribe({
        next: (response: any) => {
          this.successMessage = 'User updated successfully!';
          this.closeUserModal();
          this.loadUsers();
          this.isSubmitting = false;
        },
        error: (error: any) => {
          console.error('Error updating user:', error);
          this.errorMessage = error.error?.message || 'Failed to update user. Please try again.';
          this.isSubmitting = false;
        }
      });
    } else {
      // Create new user
      const createData: RegisterRequest = {
        username: this.currentUser.username!,
        password: this.currentUser.password!,
        firstName: this.currentUser.firstName!,
        lastName: this.currentUser.lastName!,
        email: this.currentUser.email,
        role: this.currentUser.role as 'admin' | 'user' | 'shopee_docs' | 'ShopeeChecker'
      };

      this.userService.createUser(createData).subscribe({
        next: (response: any) => {
          this.successMessage = 'User created successfully!';
          this.closeUserModal();
          this.loadUsers();
          this.isSubmitting = false;
        },
        error: (error: any) => {
          console.error('Error creating user:', error);
          this.errorMessage = error.error?.message || 'Failed to create user. Please try again.';
          this.isSubmitting = false;
        }
      });
    }
  }

  deleteUser(): void {
    if (!this.userToDelete) return;

    this.isDeleting = true;

    this.userService.deleteUser(this.userToDelete.id).subscribe({
      next: () => {
        this.successMessage = 'User deleted successfully!';
        this.closeDeleteModal();
        this.loadUsers();
        this.isDeleting = false;
      },
      error: (error: any) => {
        console.error('Error deleting user:', error);
        this.errorMessage = 'Failed to delete user. Please try again.';
        this.isDeleting = false;
      }
    });
  }

  // Security-related methods
  viewUserSecurityProfile(user: User): void {
    this.selectedUserForSecurity = user;
    this.showSecurityModal = true;
    this.isLoadingSecurityProfile = true;
    this.userSecurityProfile = null;
    
    this.securityService.getUserSecurityProfile(user.id!).subscribe({
      next: (profile) => {
        this.userSecurityProfile = profile;
        this.isLoadingSecurityProfile = false;
      },
      error: (error: any) => {
        console.error('Error loading security profile:', error);
        this.isLoadingSecurityProfile = false;
        this.errorMessage = 'Failed to load security profile';
      }
    });
  }

  closeSecurityModal(): void {
    this.showSecurityModal = false;
    this.selectedUserForSecurity = null;
    this.userSecurityProfile = null;
  }

  lockUserFromProfile(): void {
    if (this.selectedUserForSecurity) {
      this.lockUser(this.selectedUserForSecurity);
    }
  }

  unlockUserFromProfile(): void {
    if (this.selectedUserForSecurity) {
      this.unlockUser(this.selectedUserForSecurity);
    }
  }

  invalidateAllSessionsForUser(): void {
    if (this.selectedUserForSecurity && 
        confirm(`Are you sure you want to invalidate all sessions for ${this.selectedUserForSecurity.firstName} ${this.selectedUserForSecurity.lastName}?`)) {
      
      this.securityService.invalidateAllUserSessions(this.selectedUserForSecurity.id!).subscribe({
        next: () => {
          this.successMessage = 'All user sessions have been invalidated';
          // Refresh the security profile
          this.viewUserSecurityProfile(this.selectedUserForSecurity!);
        },
        error: (error: any) => {
          this.errorMessage = 'Failed to invalidate user sessions';
          console.error('Invalidate sessions error:', error);
        }
      });
    }
  }

  invalidateSpecificSession(sessionId: string): void {
    if (confirm('Are you sure you want to invalidate this session?')) {
      this.securityService.invalidateSession(sessionId).subscribe({
        next: () => {
          this.successMessage = 'Session has been invalidated';
          // Refresh the security profile
          if (this.selectedUserForSecurity) {
            this.viewUserSecurityProfile(this.selectedUserForSecurity);
          }
        },
        error: (error: any) => {
          this.errorMessage = 'Failed to invalidate session';
          console.error('Invalidate session error:', error);
        }
      });
    }
  }

  lockUser(user: User): void {
    if (confirm(`Are you sure you want to lock the account for ${user.firstName} ${user.lastName}?`)) {
      this.userService.lockUser(user.id!, 'Manual lock by administrator').subscribe({
        next: () => {
          this.successMessage = `User ${user.firstName} ${user.lastName} has been locked successfully`;
          this.loadUsers();
        },
        error: (error) => {
          this.errorMessage = 'Failed to lock user account';
          console.error('Lock user error:', error);
        }
      });
    }
  }

  unlockUser(user: User): void {
    if (confirm(`Are you sure you want to unlock the account for ${user.firstName} ${user.lastName}?`)) {
      this.userService.unlockUser(user.id!).subscribe({
        next: () => {
          this.successMessage = `User ${user.firstName} ${user.lastName} has been unlocked successfully`;
          this.loadUsers();
        },
        error: (error) => {
          this.errorMessage = 'Failed to unlock user account';
          console.error('Unlock user error:', error);
        }
      });
    }
  }

  // Utility methods
  getUserInitials(user: User): string {
    return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
  }

  getRoleDisplayName(role: string): string {
    const roleConfig = this.availableRoles.find(r => r.key === role);
    return roleConfig ? roleConfig.name : role;
  }

  getRoleBadgeClass(role: string): string {
    switch (role) {
      case 'admin':
        return 'bg-red-50 text-red-700 ring-1 ring-red-200';
      case 'shopee_docs':
        return 'bg-blue-50 text-blue-700 ring-1 ring-blue-200';
      case 'ShopeeChecker':
        return 'bg-amber-50 text-amber-800 ring-1 ring-amber-200';
      case 'ForwardingAdmin':
        return 'bg-accent-light text-accent ring-1 ring-blue-200/70';
      case 'ForwardingUser':
        return 'bg-blue-50 text-blue-700 ring-1 ring-blue-200';
      case 'user':
        return 'bg-stone-100 text-stone-700 ring-1 ring-stone-200';
      default:
        return 'bg-stone-100 text-stone-700 ring-1 ring-stone-200';
    }
  }

  trackByUserId(index: number, user: User): number {
    return user.id;
  }

  // Enhanced session management helper methods
  getDeviceTypeFromUserAgent(userAgent: string): string {
    if (!userAgent) return 'Unknown Device';
    
    if (/Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
      if (/iPad|Tablet/i.test(userAgent)) return 'Tablet';
      return 'Mobile Device';
    }
    return 'Desktop Computer';
  }

  getBrowserFromUserAgent(userAgent: string): string {
    if (!userAgent) return 'Unknown Browser';
    
    if (userAgent.includes('Chrome/') && !userAgent.includes('Edg')) return 'Chrome';
    if (userAgent.includes('Firefox/')) return 'Firefox';
    if (userAgent.includes('Safari/') && !userAgent.includes('Chrome')) return 'Safari';
    if (userAgent.includes('Edg/')) return 'Edge';
    if (userAgent.includes('Opera/') || userAgent.includes('OPR/')) return 'Opera';
    if (userAgent.includes('Trident/') || userAgent.includes('MSIE')) return 'Internet Explorer';
    
    return 'Unknown Browser';
  }

  getOperatingSystemFromUserAgent(userAgent: string): string {
    if (!userAgent) return 'Unknown OS';
    
    if (userAgent.includes('Windows NT')) return 'Windows';
    if (userAgent.includes('Mac OS X')) return 'macOS';
    if (userAgent.includes('Linux') && !userAgent.includes('Android')) return 'Linux';
    if (userAgent.includes('Android')) return 'Android';
    if (userAgent.includes('iPhone OS') || userAgent.includes('iOS')) return 'iOS';
    
    return 'Unknown OS';
  }

  isPrivateIP(ipAddress: string): boolean {
    if (!ipAddress) return false;
    
    // Check for private IP ranges
    if (ipAddress.startsWith('192.168.') || 
        ipAddress.startsWith('10.') || 
        ipAddress.startsWith('172.')) {
      return true;
    }
    
    // Check for localhost
    if (ipAddress === '127.0.0.1' || ipAddress === '::1') {
      return true;
    }
    
    return false;
  }

  getLocationFromIP(ipAddress: string): string {
    // For now, return a placeholder. In a real implementation,
    // you would integrate with an IP geolocation service
    if (this.isPrivateIP(ipAddress)) {
      return 'Local Network';
    }
    
    // You could integrate with services like:
    // - MaxMind GeoIP2
    // - ipapi.co
    // - ipgeolocation.io
    return 'Unknown Location';
  }

  getSessionRiskLevel(session: any): string {
    let riskScore = 0;
    
    // Check for unusual IP
    if (!this.isPrivateIP(session.ipAddress)) {
      riskScore += 1;
    }
    
    // Check session age (older sessions might be riskier)
    const sessionAge = new Date().getTime() - new Date(session.createdAtUtc).getTime();
    const hoursOld = sessionAge / (1000 * 60 * 60);
    if (hoursOld > 24) riskScore += 1;
    if (hoursOld > 72) riskScore += 1;
    
    // Check for unusual user agents (bots, scrapers, etc.)
    if (this.isUnusualUserAgent(session.userAgent)) {
      riskScore += 2;
    }
    
    // Check last activity
    if (session.lastActivityAtUtc) {
      const lastActivity = new Date().getTime() - new Date(session.lastActivityAtUtc).getTime();
      const inactiveHours = lastActivity / (1000 * 60 * 60);
      if (inactiveHours > 12) riskScore += 1;
    }
    
    if (riskScore >= 3) return 'High';
    if (riskScore >= 2) return 'Medium';
    return 'Low';
  }

  getSessionRiskBadgeClass(session: any): string {
    const riskLevel = this.getSessionRiskLevel(session);
    switch (riskLevel) {
      case 'High': return 'bg-red-50 dark:bg-red-500/15 text-red-700 dark:text-red-400 ring-1 ring-red-200 dark:ring-red-500/30';
      case 'Medium': return 'bg-amber-50 dark:bg-amber-500/15 text-amber-800 dark:text-amber-400 ring-1 ring-amber-200 dark:ring-amber-500/30';
      case 'Low': return 'bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-400 ring-1 ring-green-200 dark:ring-green-500/30';
      default: return 'bg-stone-100 dark:bg-stone-700 text-stone-700 dark:text-stone-300 ring-1 ring-stone-200 dark:ring-stone-600';
    }
  }

  private isUnusualUserAgent(userAgent: string): boolean {
    if (!userAgent) return true;
    
    // Check for automated tools and bots
    const suspiciousPatterns = [
      'bot', 'crawler', 'spider', 'scraper', 'python', 'curl', 'wget', 
      'postman', 'insomnia', 'httpie', 'libcurl', 'java/', 'apache-httpclient'
    ];
    
    const lowerUserAgent = userAgent.toLowerCase();
    return suspiciousPatterns.some(pattern => lowerUserAgent.includes(pattern));
  }

  getTimeAgo(dateString: string): string {
    if (!dateString) return 'Unknown';
    
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffMins > 0) {
      return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  }

  // Statistics methods
  getAdminCount(): number {
    return this.users.filter(user => user.role === 'admin').length;
  }

  getShopeeDocsCount(): number {
    return this.users.filter(user => user.role === 'shopee_docs').length;
  }

  getShopeeCheckerCount(): number {
    return this.users.filter(user => user.role === 'ShopeeChecker').length;
  }

  getActiveUsersCount(): number {
    return this.users.filter(user => user.role === 'user').length;
  }

  getForwardingAdminCount(): number {
    return this.users.filter(user => user.role === 'ForwardingAdmin').length;
  }

  getForwardingUserCount(): number {
    return this.users.filter(user => user.role === 'ForwardingUser').length;
  }

  // Export functionality
  exportUsers(): void {
    const csvContent = this.generateCSV();
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'users-export.csv';
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private generateCSV(): string {
    const headers = ['ID', 'First Name', 'Last Name', 'Username', 'Email', 'Role'];
    const rows = this.users.map(user => [
      user.id.toString(),
      user.firstName,
      user.lastName,
      user.username,
      user.email,
      this.getRoleDisplayName(user.role)
    ]);

    return [headers, ...rows]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');
  }

  // Message management
  clearMessages(): void {
    this.successMessage = '';
    this.errorMessage = '';
  }

  // Test function for debugging
  testFunction(): void {
    console.log('Test function called!');
    alert('Test function works!');
  }

  // Modal backdrop click handlers
  onModalBackdropClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.closeUserModal();
    }
  }

  onDeleteModalBackdropClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.closeDeleteModal();
    }
  }
}




